import React from 'react';
import { getDetailsForReceiptPrint } from '../helpers/ReceiptHelper';
import { getImageURL } from "../helpers/ImageHelper";
import { useTranslation } from 'react-i18next';

/**
 * PrintKOTPage — 57mm professional KOT with notes printing logic matching bill
 */
export default function PrintKOTPage() {
  const { t } = useTranslation();
  const data = getDetailsForReceiptPrint() || {};

  const {
    cartItems = [],
    tokenNo: tokenFromData = '',
    orderId = '',
    storeSettings = {},
    printSettings = {},
    currency = '',
    // optional boolean flags may come from data or printSettings
    isTokenNumberEnabled: tokenFlagFromData = data.isTokenNumberEnabled,
    isHotelModeEnabled: hotelFlagFromData = data.isHotelModeEnabled,
  } = data;

  // replicate bill logic: show_notes flag controls whether notes are printed
  const showNotesFlag = (printSettings && (printSettings.show_notes === 1 || printSettings.showNotes === 1)) || false;

  // token config normalization (accept several possible keys)
  const isTokenNumberEnabled =
    (printSettings && (printSettings.isTokenNumberEnabled === true || printSettings.is_token_enabled === true)) ||
    (printSettings && printSettings.print_token === 1) ||
    tokenFlagFromData === true;

  const isHotelModeEnabled =
    (printSettings && (printSettings.isHotelModeEnabled === true || printSettings.is_hotel_mode === true)) ||
    hotelFlagFromData === true;

  const companyName = (data.companyName || data.personalInformationModel?.companyName || storeSettings?.store_name) || 'Your Co.';
  const countryName = data.personalInformationModel?.countryName || storeSettings?.country || '';
  const tableId = data.transitionModel?.tableId || data.tableId || '';
  const tableTitle = data.table_title || data.tableTitle || '';
  const deliveryType = data.deliveryType || data.delivery_type || '';
  const source = data.source || data.room_name || '';
  const isRoomOrder = deliveryType === 'room' || !!source || (tableTitle && tableTitle.toLowerCase().includes('room'));
  const invoiceNumber = data.transitionModel?.invoiceNumber || data.invoiceNumber || orderId || '';
  const waiterName = data.waiterName || data.waiter_name || data.waiter || data.userName || data.created_by || storeSettings?.waiter_name || '';

  let diningLabel = '';
  if (isRoomOrder) {
    const rName = source || tableTitle.replace(/^room\s*/i, '') || 'Room';
    diningLabel = rName.toLowerCase().startsWith('room') ? rName.toUpperCase() : `ROOM: ${rName.toUpperCase()}`;
  } else if (tableTitle || tableId) {
    diningLabel = `TABLE: ${(tableTitle || tableId).toString().toUpperCase()}`;
  } else if (deliveryType) {
    diningLabel = deliveryType.toUpperCase();
  }

  // page width (default 57mm)
  const pageW = Number(String(printSettings?.page_format || "").replace(/,/g, "")) || 57;

  // fonts and layout tuned for 57mm
  const NAME_FONT = 15; // item name px
  const QTY_FONT = 18;  // qty px
  const NOTE_FONT = 12; // notes px
  const COMPANY_FONT = 17;
  const TOKEN_FONT = 24;
  const QTY_COL = 46;   // fixed qty column width (px)

  const now = new Date();
  const dateIso = now.toISOString().substring(0, 10);
  const timeLocale = now.toLocaleTimeString();

  // final token to print (falls back to tokenFromData)
  const tokenToPrint = tokenFromData || data.completeTokenIds || data.token || '';

  return (
    <>
      <style>{`
        .kot-root { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; color:#000; background:#fff; }
        .kot-inner { box-sizing: border-box; padding: 6px 6px; width: ${pageW}mm; max-width: ${pageW}mm; }

        .center { text-align:center; }

        /* Company & meta */
        .company { font-weight:900; font-size:${COMPANY_FONT}px; letter-spacing:0.6px; }
        .country-line { font-size:11px; font-weight:700; color:#444; margin-top:4px; }

        .hr { border-bottom:1px dashed #444; margin:6px 0; }

        .invoice-center { font-weight:800; font-size:12px; margin-top:4px; }

        .table-line { font-weight:800; font-size:13px; margin-top:4px; color:#222; }

        /* KOT Title */
        .kot-title { font-weight:900; font-size:18px; margin:8px 0; text-align:center; letter-spacing:1px; }

        /* Token block */
        .token-block { text-align:center; margin-top:6px; margin-bottom:6px; }
        .token-label { font-weight:800; font-size:12px; }
        .token-circle {
          width: 96px;
          height: 96px;
          border-radius: 999px;
          border: 3px solid #000;
          display:flex;
          align-items:center;
          justify-content:center;
          font-weight:900;
          font-size: ${TOKEN_FONT}px;
          margin: 6px auto;
        }

        /* Items grid: qty fixed column on left so qty sits very close to item name on right */
        .items { margin-top:6px; }
        .item-row {
          display: grid;
          grid-template-columns: ${QTY_COL}px 1fr;
          column-gap: 6px;
          align-items: start;
          margin:6px 0;
        }
        .item-qty {
          font-size: ${QTY_FONT}px;
          font-weight:900;
          text-align:center;
          padding: 2px 2px;
          border: 1.5px solid #000;
          border-radius: 4px;
        }
        .item-name {
          font-size: ${NAME_FONT}px;
          font-weight:800;
          line-height:1.15;
          word-break: break-word;
        }

        /* Addons and notes: notes printed only when showNotesFlag true */
        .item-addons {
          margin-left: ${QTY_COL + 6}px; /* align under name */
          font-size: ${NOTE_FONT}px;
          font-weight:700;
          color:#333;
          margin-top: 4px;
        }
        .item-notes {
          margin-left: ${QTY_COL + 6}px;
          font-size: ${NOTE_FONT}px;
          font-weight:700;
          color:#222;
          white-space: pre-wrap; /* preserve line breaks exactly as bill */
          margin-top: 4px;
        }

        /* final separator using = character visual */
        .equals-hr { font-family: monospace; text-align:center; font-weight:900; margin:6px 0; letter-spacing:1px; color:#333; }

        .footer-note { text-align:center; font-weight:700; font-size:11px; margin-top:6px; color:#444; }

        /* print scoping */
        @media print {
          @page { margin: 1mm; }
          body { width: ${pageW}mm; }
          body * { visibility: hidden !important; }
          .kot-root, .kot-root * { visibility: visible !important; }
          .kot-root { position: absolute; left: 0; top: 0; page-break-after: always; -webkit-print-color-adjust: exact; width: ${pageW}mm; }
        }

        html, body { background: #fff; margin:0; padding:0; width: ${pageW}mm; }
      `}</style>

      <div className="kot-root" role="document" aria-label="KOT">
        <div className="kot-inner">
          {/* Company & country */}
          <div className="center" aria-hidden={false}>
            <div className="company">{companyName}</div>
            {countryName ? <div className="country-line">{countryName}</div> : null}
          </div>

          <div className="hr" />

          {/* Invoice */}
          {invoiceNumber ? (
            <div className="invoice-center center">Invoice# {invoiceNumber}</div>
          ) : null}

          {/* Token block: only render when enabled and token value exists */}
          {isTokenNumberEnabled && tokenToPrint ? (
            <div className="token-block" role="group" aria-label="token-block">
              <div className="token-label">{t('print_receipt.token') || 'Token'}</div>
              <div className="token-circle" aria-label="token-number">{tokenToPrint}</div>
            </div>
          ) : null}

          {/* Table / Room / Dining option */}
          {diningLabel ? (
            <div className="center table-line" style={{ fontWeight: 900, fontSize: '15px', margin: '4px 0', border: '2px solid #000', padding: '4px', textTransform: 'uppercase' }}>
              {diningLabel}
            </div>
          ) : null}

          {/* Waiter / Punched By */}
          {waiterName ? (
            <div className="center waiter-line" style={{ fontWeight: 900, fontSize: '12px', margin: '4px 0', border: '1.5px solid #000', padding: '3px 4px', textTransform: 'uppercase' }}>
              WAITER: {waiterName}
            </div>
          ) : null}

          <div className="hr" />

          {/* KOT Title */}
          <div className="kot-title">KOT</div>
          <div className="hr" />

          {/* Items */}
          <div className="items" aria-label="kot-items">
            {Array.isArray(cartItems) && cartItems.length > 0 ? (
              cartItems.map((it, idx) => {
                const name = (it.title || it.productName || it.name || 'Item').toString();
                const qty = (it.quantity ?? it.qty ?? 0).toString();
                const variantTitle = (it.variant && (it.variant.title || it.variant)) || it.variant_title || '';
                const addonsArr = it.addons || [];
                const addonsText = (it.addons_ids || []).length
                  ? (it.addons || []).filter(a => it.addons_ids.includes(a.id)).map(a => a.title).join(', ')
                  : (addonsArr.length ? addonsArr.map(a => a.title).join(', ') : '');
                const notes = it.notes || '';

                return (
                  <div key={it.id ?? idx} style={{marginBottom:6}}>
                    <div className="item-row" aria-label={`item-${idx}`}>
                      <div className="item-qty" aria-label={`qty-${idx}`}>{qty}</div>
                      <div>
                        <div className="item-name" title={variantTitle ? `${name} - ${variantTitle}` : name}>
                          {name}{variantTitle ? ` - ${variantTitle}` : ''}
                        </div>

                        {/* Addons printed always (like bill) */}
                        {addonsText ? <div className="item-addons">Addons: {addonsText}</div> : null}

                        {/* Notes printed only when show_notes flag is enabled on print settings (matches bill) */}
                        {showNotesFlag && notes ? <div className="item-notes">{notes}</div> : null}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="item-row">
                <div className="item-qty">0</div>
                <div><div className="item-name">No items</div></div>
              </div>
            )}
          </div>

          {/* equals HR and footer */}
          <div className="equals-hr">{'='.repeat(28)}</div>

          <div className="footer-note">Please prepare items as listed above</div>

          {/* force page break */}
          <div style={{display:'block', pageBreakAfter:'always'}} />
        </div>
      </div>
    </>
  );
}
